import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useFinanceEntries,
  useFinanceSummary,
  useCreateFinanceEntry,
  useFinanceCategories,
  useCreateFinanceCategory,
} from '../../../platform/api/hooks';
import { asList, asRecord, str, getMeta } from '../../../platform/api/utils';
import { PageHeader } from '../../../components/ui/PageHeader';
import { Badge } from '../../../components/ui/Badge';
import { Section } from '../../../components/ui/Section';
import {
  type CreateForm,
  TYPE_LABELS,
  TYPE_VARIANTS,
  STATUS_LABELS,
  STATUS_VARIANTS,
  fmtCurrency,
  createFormSchema,
  SummaryCard,
  Th,
  Td,
  PagBtn,
  Skeleton,
} from './finance-list.support';
import { FinanceCreateModal } from './finance-list.modals';

let idCounter = 0;

export function FinanceList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);

  const params: Record<string, unknown> = { page, limit: 20 };
  if (search) params.search = search;
  if (statusFilter) params.status = statusFilter;
  if (typeFilter) params.type = typeFilter;

  const { data, isLoading, isError } = useFinanceEntries(params);
  const { data: summary } = useFinanceSummary();
  const createEntry = useCreateFinanceEntry();
  const createCategory = useCreateFinanceCategory();
  const { data: catData } = useFinanceCategories();
  const categories = asList(catData);

  const entries = asList(data);
  const meta = getMeta(data);
  const s = asRecord(summary);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCatName, setNewCatName] = useState('');

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const form = useForm<CreateForm>({
    resolver: zodResolver(createFormSchema),
    defaultValues: {
      type: 'EXPENSE',
      description: '',
      amount: '',
      categoryId: '',
      dueDate: '',
      personId: '',
      paymentMethod: '',
      receiptRef: '',
    },
  });

  const selectedType = form.watch('type');
  const categoriesForType = categories.filter((c) => {
    const t = str(asRecord(c).type);
    return !t || t === selectedType;
  });

  function handleCreate(data: CreateForm) {
    createEntry.mutate({
      type: data.type,
      description: data.description,
      amount: data.amount,
      categoryId: data.categoryId !== '' ? data.categoryId : undefined,
      dueDate: data.dueDate !== '' ? data.dueDate : undefined,
      personId: data.personId !== '' ? data.personId : undefined,
      paymentMethod: data.paymentMethod !== '' ? data.paymentMethod : undefined,
      receiptRef: data.receiptRef !== '' ? data.receiptRef : undefined,
    });
    setShowCreate(false);
    form.reset();
  }

  function fmtDate(v: unknown): string {
    if (typeof v === 'string' && v.length >= 10) return v.substring(0, 10);
    if (v instanceof Date) return v.toISOString().substring(0, 10);
    return '—';
  }

  if (isError) {
    return (
      <div style={{ padding: '2.25rem 2.5rem', maxWidth: 1320 }}>
        <PageHeader title="Financeiro" subtitle="Entradas, saídas e contribuições" />
        <Section title="Erro">
          <p style={{ color: 'var(--danger)', fontSize: '0.875rem', margin: 0 }}>
            Não foi possível carregar os lançamentos financeiros.
          </p>
        </Section>
      </div>
    );
  }

  const totalPages = Number(meta.totalPages) || 1;

  return (
    <div style={{ padding: '2.25rem 2.5rem', maxWidth: 1320 }}>
      <PageHeader
        title="Financeiro"
        subtitle="Entradas, saídas e contribuições"
        action={{
          label: '+ Novo lançamento',
          onClick: () => {
            setShowCreate(true);
          },
        }}
      />

      {/* Summary cards — fields aligned with /finance/summary */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <SummaryCard label="Saldo" value={fmtCurrency(s.balance)} color="var(--success)" />
        <SummaryCard
          label="Entradas"
          value={fmtCurrency(s.totalIncome ?? s.income)}
          color="var(--success)"
        />
        <SummaryCard
          label="Saídas"
          value={fmtCurrency(s.totalExpense ?? s.expense)}
          color="var(--danger)"
        />
        <SummaryCard
          label="Contribuições"
          value={fmtCurrency(s.contribution)}
          color="var(--info)"
        />
        <SummaryCard
          label="Pendentes"
          value={String(Number(s.pendingCount ?? s.pendingTotal ?? 0))}
          color="var(--warning)"
          accent
        />
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Buscar por descrição..."
          value={search}
          onChange={(e) => {
            handleSearch(e.target.value);
          }}
          style={{
            flex: 1,
            maxWidth: 280,
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--line)',
            borderRadius: 10,
            fontSize: '0.875rem',
            outline: 'none',
          }}
        />
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          style={{
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--line)',
            borderRadius: 10,
            fontSize: '0.875rem',
            outline: 'none',
            background: '#fff',
          }}
        >
          <option value="">Todos os status</option>
          {Object.entries(STATUS_LABELS).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value);
            setPage(1);
          }}
          style={{
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--line)',
            borderRadius: 10,
            fontSize: '0.875rem',
            outline: 'none',
            background: '#fff',
          }}
        >
          <option value="">Todos os tipos</option>
          {Object.entries(TYPE_LABELS).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
      </div>

      {/* Skeleton */}
      {isLoading && <Skeleton />}

      {/* Tabela */}
      {!isLoading && (
        <Section title="Lançamentos">
          {entries.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '3rem 1rem',
                color: 'var(--faint)',
                fontSize: '0.875rem',
              }}
            >
              {search || statusFilter || typeFilter
                ? 'Nenhum lançamento encontrado com esses filtros.'
                : 'Nenhum lançamento financeiro registrado ainda.'}
            </div>
          ) : (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--line-soft)' }}>
                    <Th>Data</Th>
                    <Th>Descrição</Th>
                    <Th>Tipo</Th>
                    <Th>Categoria</Th>
                    <Th>Valor</Th>
                    <Th>Situação</Th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((raw) => {
                    const e = asRecord(raw);
                    const st = str(e.status);
                    const tp = str(e.type);
                    const cat = e.category as Record<string, unknown> | undefined;
                    return (
                      <tr
                        key={str(e.id, String(idCounter++))}
                        onClick={() => {
                          void navigate(`/financeiro/${String(e.id)}`);
                        }}
                        style={{
                          cursor: 'pointer',
                          borderBottom: '1px solid var(--soft)',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={(ev) => {
                          (ev.currentTarget as HTMLElement).style.background = 'var(--paper)';
                        }}
                        onMouseLeave={(ev) => {
                          (ev.currentTarget as HTMLElement).style.background = 'transparent';
                        }}
                      >
                        <Td style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                          {fmtDate(e.dueDate)}
                        </Td>
                        <Td>
                          <span style={{ fontWeight: 500, color: 'var(--green)' }}>
                            {str(e.description)}
                          </span>
                        </Td>
                        <Td>
                          <Badge
                            label={TYPE_LABELS[tp] ?? tp}
                            variant={TYPE_VARIANTS[tp] ?? 'default'}
                          />
                        </Td>
                        <Td style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                          {cat ? str(cat.name) : '—'}
                        </Td>
                        <Td
                          style={{
                            fontWeight: 600,
                            color: tp === 'EXPENSE' ? 'var(--danger)' : 'var(--success)',
                          }}
                        >
                          {fmtCurrency(e.amount)}
                        </Td>
                        <Td>
                          <Badge
                            label={STATUS_LABELS[st] ?? st}
                            variant={STATUS_VARIANTS[st] ?? 'default'}
                          />
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {totalPages > 1 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '0.25rem',
                    marginTop: '1rem',
                  }}
                >
                  <PagBtn
                    disabled={page <= 1}
                    onClick={() => {
                      setPage(page - 1);
                    }}
                    label="<"
                  />
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <PagBtn
                      key={p}
                      active={p === page}
                      onClick={() => {
                        setPage(p);
                      }}
                      label={String(p)}
                    />
                  ))}
                  <PagBtn
                    disabled={page >= totalPages}
                    onClick={() => {
                      setPage(page + 1);
                    }}
                    label=">"
                  />
                </div>
              )}
            </>
          )}
        </Section>
      )}

      {showCreate && (
        <FinanceCreateModal
          form={form}
          categoriesForType={categoriesForType}
          selectedType={selectedType}
          showNewCategory={showNewCategory}
          setShowNewCategory={setShowNewCategory}
          newCatName={newCatName}
          setNewCatName={setNewCatName}
          createCategory={createCategory}
          createPending={createEntry.isPending}
          onCancel={() => {
            setShowCreate(false);
            form.reset();
            setShowNewCategory(false);
          }}
          onCreate={() => {
            void form.handleSubmit(handleCreate)();
          }}
        />
      )}
    </div>
  );
}
